import { useState, useEffect } from 'react';
import { SupabaseClient, type Session } from '@supabase/supabase-js';
import { Routes, Route, useNavigate } from 'react-router-dom'; // Import routing components
import LandingPage from './pages/LandingPage'; // Import the new LandingPage component
import Dashboard from './pages/Dashboard';
import Auth from './components/Auth/Auth'; // Import the Auth component
import PaymentSuccess from './components/PaymentSuccess/PaymentSuccess'; // Import PaymentSuccess component
import './App.css'; // Keep App.css for any global app-level styles if needed, or remove if not.

interface AppProps {
  supabase: SupabaseClient;
}

function App({ supabase }: AppProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true); // New loading state for initial auth check
  const navigate = useNavigate(); // Initialize useNavigate hook

  console.log('App.tsx: Initial render - session:', session, 'loading:', loading);

  useEffect(() => {
    console.log('App.tsx: useEffect - checking session...');
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('App.tsx: getSession result - session:', session);
      setSession(session);
      setLoading(false); // Set loading to false after initial session check
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('App.tsx: onAuthStateChange event - event:', _event, 'session:', session);
      setSession(session);
      setLoading(false); // Also set loading to false on auth state change
    });

    return () => {
      console.log('App.tsx: Unsubscribing from auth state changes.');
      subscription.unsubscribe();
    };
  }, [supabase]);

  // Effect to redirect to dashboard after successful login
  useEffect(() => {
    if (session && !loading && window.location.pathname === '/login') {
      console.log('App.tsx: Session detected and on /login, navigating to /dashboard.');
      navigate('/dashboard');
    }
  }, [session, loading, navigate]);

  const handleAuthChange = () => {
    console.log('App.tsx: handleAuthChange called. Re-fetching session...');
    // This function is called by Auth component on successful login/signup
    // It triggers a re-render in App.tsx which will then show the Dashboard
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('App.tsx: handleAuthChange getSession result - session:', session);
      setSession(session);
    });
  };

  console.log('App.tsx: Rendering based on session - session:', session ? 'present' : 'null', 'loading:', loading);

  if (loading) {
    console.log('App.tsx: Displaying loading authentication message.');
    return <div className="App">Loading authentication...</div>; // Display loading state
  }

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<LandingPage />} /> {/* Landing page as default */}
        <Route path="/login" element={<Auth onAuthChange={handleAuthChange} />} /> {/* Login page */}
        <Route path="/dashboard" element={session ? <Dashboard /> : <Auth onAuthChange={handleAuthChange} />} /> {/* Dashboard, protected */}
        <Route path="/payment-success" element={<PaymentSuccessWithRedirect />} /> {/* Payment success page */}
        {/* Add other routes here as needed */}
      </Routes>
    </div>
  );
}

// New component to handle redirect after payment success
const PaymentSuccessWithRedirect: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/'); // Redirect to dashboard or home page
    }, 5000); // Redirect after 5 seconds

    return () => clearTimeout(timer);
  }, [navigate]);

  return <PaymentSuccess />;
};

export default App;
